import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Truck, Building2, Phone, Mail, MapPin, Users, UserPlus, FileSpreadsheet, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const SUPPLIER_TYPES = [
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CRUISE', label: 'Cruise' },
  { value: 'FLIGHT', label: 'Flight' },
  { value: 'TRAIN', label: 'Train' },
  { value: 'OTHER', label: 'Other' },
];

const SupplierManager = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [places, setPlaces] = useState([]);
  const [masters, setMasters] = useState({ states: [], supplierTypes: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  
  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_type: 'TRANSPORT',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    state: '',
    place_id: '',
    gst_number: '',
    pan_number: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    contacts: []
  });

  useEffect(() => {
    fetchSuppliers();
    fetchPlaces();
    fetchMasters();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data || []);
    } catch (error) {
      console.error('Failed to load places');
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await api.get('/hotel-rates/masters');
      setMasters({
        states: response.data.states || [],
        supplierTypes: response.data.supplierTypes || SUPPLIER_TYPES.map(t => t.label)
      });
    } catch (error) {
      console.error('Failed to load masters');
    }
  };

  // Get places filtered by selected state
  const filteredPlaces = formData.state 
    ? places.filter(p => p.state === formData.state)
    : places;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.supplier_id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Supplier created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Supplier deleted successfully');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      supplier_type: 'TRANSPORT',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      state: '',
      place_id: '',
      gst_number: '',
      pan_number: '',
      bank_name: '',
      bank_account: '',
      bank_ifsc: '',
      contacts: []
    });
    setEditingSupplier(null);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_name: supplier.supplier_name || '',
      supplier_type: supplier.supplier_type || 'TRANSPORT',
      contact_name: supplier.contact_name || '',
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      address: supplier.address || '',
      state: supplier.state || '',
      place_id: supplier.place_id || '',
      gst_number: supplier.gst_number || '',
      pan_number: supplier.pan_number || '',
      bank_name: supplier.bank_name || '',
      bank_account: supplier.bank_account || '',
      bank_ifsc: supplier.bank_ifsc || '',
      contacts: supplier.contacts || []
    });
    setDialogOpen(true);
  };

  // Contact management
  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { contact_name: '', contact_email: '', contact_phone: '', designation: '', is_primary: false }]
    });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setFormData({ ...formData, contacts: newContacts });
  };

  const removeContact = (index) => {
    const newContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: newContacts });
  };

  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = s.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          s.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || filterType === 'all' || s.supplier_type === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'TRANSPORT': return <Truck className="w-4 h-4" />;
      case 'HOTEL': return <Building2 className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="supplier-manager-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Supplier Manager</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{filteredSuppliers.length} suppliers</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-suppliers-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Dialog open={bulkImportOpen} onOpenChange={(open) => { setBulkImportOpen(open); if (!open) { setImportFile(null); setValidationResult(null); setImportResult(null); if(importFileRef.current) importFileRef.current.value = ''; } }}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Import Suppliers</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Download Template</p>
                      <p className="text-xs text-gray-500">CSV format with required columns</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const response = await api.get('/admin/bulk-import/suppliers/template', { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'suppliers_template.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (error) {
                        toast.error('Failed to download template');
                      }
                    }}>
                      <Download className="w-4 h-4 mr-2" />
                      Template
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Upload File (CSV or Excel)</Label>
                    <Input
                      ref={importFileRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setImportFile(file);
                        setValidationResult(null);
                        setImportResult(null);
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const response = await api.post('/admin/bulk-import/suppliers/validate', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setValidationResult(response.data);
                        } catch (error) {
                          toast.error('Failed to validate file');
                          setValidationResult({ valid: false, errors: [error.response?.data?.detail || 'Validation failed'] });
                        }
                      }}
                    />
                  </div>
                  
                  {validationResult && (
                    <div className={`p-3 rounded-lg ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {validationResult.valid ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-red-600" />}
                        <span className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{validationResult.valid_rows} of {validationResult.total_rows} rows valid</p>
                      {validationResult.errors?.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                          {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  
                  {importResult && (
                    <div className={`p-3 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`font-medium ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {importResult.success ? `Created: ${importResult.created}, Updated: ${importResult.updated}` : 'Import Failed'}
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkImportOpen(false)}>Close</Button>
                  <Button 
                    onClick={async () => {
                      if (!importFile || !validationResult?.valid) return;
                      setImporting(true);
                      const fd = new FormData();
                      fd.append('file', importFile);
                      try {
                        const response = await api.post('/admin/bulk-import/suppliers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setImportResult(response.data);
                        if (response.data.success) {
                          toast.success(`Imported ${response.data.created} suppliers`);
                          fetchSuppliers();
                        }
                      } catch (error) {
                        toast.error('Import failed');
                      } finally {
                        setImporting(false);
                      }
                    }}
                    disabled={!validationResult?.valid || importing}
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-supplier-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Supplier
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier Name *</Label>
                    <Input
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      required
                      data-testid="supplier-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier Type</Label>
                    <Select value={formData.supplier_type} onValueChange={(v) => setFormData({ ...formData, supplier_type: v })}>
                      <SelectTrigger data-testid="supplier-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPLIER_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Location
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select 
                        value={formData.state || "none"} 
                        onValueChange={(v) => setFormData({ ...formData, state: v === "none" ? "" : v, place_id: '' })}
                      >
                        <SelectTrigger data-testid="supplier-state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select state</SelectItem>
                          {masters.states.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Place</Label>
                      <Select 
                        value={formData.place_id || "none"} 
                        onValueChange={(v) => setFormData({ ...formData, place_id: v === "none" ? "" : v })}
                        disabled={!formData.state}
                      >
                        <SelectTrigger data-testid="supplier-place-select">
                          <SelectValue placeholder={formData.state ? "Select place" : "Select state first"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select place</SelectItem>
                          {filteredPlaces.map((place) => (
                            <SelectItem key={place.place_id} value={place.place_id}>{place.place_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Address</Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      data-testid="supplier-address-input"
                    />
                  </div>
                </div>
                
                {/* Primary Contact */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-600" />
                    Primary Contact
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Contact Person</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        data-testid="primary-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Contacts */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      Additional Contacts
                    </h4>
                    <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-btn">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Contact
                    </Button>
                  </div>
                  {formData.contacts.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No additional contacts. Click "Add Contact" to add one.</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.contacts.map((contact, index) => (
                        <div key={index} className="grid grid-cols-5 gap-2 items-end p-3 bg-gray-50 rounded-lg" data-testid={`contact-row-${index}`}>
                          <div className="space-y-1">
                            <Label className="text-xs">Name *</Label>
                            <Input
                              value={contact.contact_name}
                              onChange={(e) => updateContact(index, 'contact_name', e.target.value)}
                              placeholder="Name"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Email</Label>
                            <Input
                              type="email"
                              value={contact.contact_email || ''}
                              onChange={(e) => updateContact(index, 'contact_email', e.target.value)}
                              placeholder="Email"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Phone</Label>
                            <Input
                              value={contact.contact_phone || ''}
                              onChange={(e) => updateContact(index, 'contact_phone', e.target.value)}
                              placeholder="Phone"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Designation</Label>
                            <Input
                              value={contact.designation || ''}
                              onChange={(e) => updateContact(index, 'designation', e.target.value)}
                              placeholder="Designation"
                              className="h-9"
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(index)} className="h-9 text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tax & Banking */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Tax & Banking Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>GST Number</Label>
                      <Input
                        value={formData.gst_number}
                        onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                        placeholder="e.g., 32AABCT1234N1ZV"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>PAN Number</Label>
                      <Input
                        value={formData.pan_number}
                        onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                        placeholder="e.g., AABCT1234N"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={formData.bank_account}
                        onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IFSC Code</Label>
                      <Input
                        value={formData.bank_ifsc}
                        onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="save-supplier-btn">
                    {editingSupplier ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search suppliers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="supplier-search-input"
                />
              </div>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {SUPPLIER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No suppliers found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Supplier</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Location</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Contact</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Contacts</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuppliers.map((supplier) => (
                      <tr key={supplier.supplier_id} className="border-b hover:bg-gray-50" data-testid={`supplier-row-${supplier.supplier_id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              {getTypeIcon(supplier.supplier_type)}
                            </div>
                            <div>
                              <span className="font-medium">{supplier.supplier_name}</span>
                              {supplier.gst_number && (
                                <p className="text-xs text-gray-500">GST: {supplier.gst_number}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="secondary">{supplier.supplier_type}</Badge>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {supplier.state && (
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className="w-3 h-3" />
                              {supplier.place_name ? `${supplier.place_name}, ${supplier.state}` : supplier.state}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {supplier.contact_name && (
                            <div className="text-sm">
                              <p className="font-medium">{supplier.contact_name}</p>
                              {supplier.contact_phone && (
                                <p className="text-gray-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {supplier.contact_phone}
                                </p>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {supplier.contacts && supplier.contacts.length > 0 ? (
                            <Badge variant="outline">{supplier.contacts.length} contacts</Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(supplier)} title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(supplier.supplier_id)} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SupplierManager;
